/**
 * Transforms raw breadcrumb data from GraphQL into a registry organized by language and path
 *
 * This function is called at runtime during Layout initialization to create a lookup structure
 * that enables fast breadcrumb retrieval. The breadcrumb data comes from a static GraphQL query
 * that is populated during Gatsby build when BreadcrumbNodes are created in createPages.ts.
 *
 * @param allBreadcrumbNode - Raw breadcrumb nodes from GraphQL static query
 * @returns Registry mapping language → path → breadcrumbs array
 *
 * @example
 * // Input: GraphQL nodes
 * { nodes: [
 *   { language: 'en', path: '/en/integrations', breadcrumbs: [{ path: '/en', text: 'Home' }, { text: 'Integrations' }] },
 *   { language: 'fr', path: '/fr/integrations', breadcrumbs: [{ path: '/fr', text: 'Accueil' }, { text: 'Intégrations' }] }
 * ]}
 *
 * // Output: Organized registry
 * {
 *   en: { '/en/integrations': [{ path: '/en', text: 'Home' }, { text: 'Integrations' }] },
 *   fr: { '/fr/integrations': [{ path: '/fr', text: 'Accueil' }, { text: 'Intégrations' }] }
 * }
 */
const getBreadcrumbs = (
  allBreadcrumbNode: Queries.GeneralContextQuery['allBreadcrumbNode']
): BreadcrumbRegistry => {
  const breadcrumbs: BreadcrumbRegistry =
    allBreadcrumbNode?.nodes.reduce((all, current) => {
      if (!(current.language in all)) {
        all[current.language] = {};
      }

      // Filter out null paths from breadcrumb items (last item typically has no path)
      const filteredBreadcrumbs = current.breadcrumbs.map((breadcrumb) => {
        if (breadcrumb.path === null) {
          return { text: breadcrumb.text };
        }
        return breadcrumb;
      }) as Queries.BreadcrumbItem[];

      all[current.language][current.path] = filteredBreadcrumbs;
      return all;
    }, {} as BreadcrumbRegistry) || {};

  return breadcrumbs;
};

export default getBreadcrumbs;
